import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { cardStyle, buttonPrimary } from "../styles/ui";
import {
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineClipboardDocumentCheck,
  HiOutlineClock,
  HiOutlineBolt,
  HiOutlineArrowPath,
  HiOutlineMagnifyingGlass,
  HiOutlineTrophy,
  HiOutlineExclamationTriangle,
  HiOutlineSparkles,
  HiOutlineHandThumbUp,
  HiOutlineXMark,
  HiOutlineListBullet
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#CCFBF1",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

const DAILY_TARGET = 40;

function formatDateInput(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDisplayDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function getHalfCycleKey(dateValue) {
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const half = d.getDate() <= 15 ? 1 : 2;
  return `${year}-${month}-${half}`;
}

function ManagerDashboard({ user, setPage, setSelectedEmployee }) {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 17);

  const [startDate, setStartDate] = useState(formatDateInput(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(formatDateInput(today));
  const [search, setSearch] = useState("");

  const [teamRows, setTeamRows] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appreciatingId, setAppreciatingId] = useState(null);

  const [appreciationHistory, setAppreciationHistory] = useState([]);
  const [leadersOpen, setLeadersOpen] = useState(false);
  const [leaderSearch, setLeaderSearch] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
const [errorMessage, setErrorMessage] = useState("");

 useEffect(() => {
  loadDashboard();
}, []);

useEffect(() => {
  if (successMessage || errorMessage) {
    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);

    return () => clearTimeout(timer);
  }
}, [successMessage, errorMessage]);


  const loadDashboard = async () => {
    try {
      setLoading(true);

      const [teamRes, prodRes, reqRes, appRes] = await Promise.all([
        api.get(`/manager/team/${user.id}?view=monthly`),
        api.get("/production"),
        api.get(`/requests/manager/${user.id}`),
        api.get("/appreciations/manager-history")
      ]);

      const teamBaseRows = Array.isArray(teamRes.data) ? teamRes.data : [];
      const productionRows = Array.isArray(prodRes.data) ? prodRes.data : [];
      const requestRows = Array.isArray(reqRes.data) ? reqRes.data : [];
      const appreciationRows = Array.isArray(appRes.data) ? appRes.data : [];

      setRequests(requestRows);
      setAppreciationHistory(appreciationRows);

      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const filteredProduction = productionRows.filter((row) => {
        const d = new Date(row.date);
        return d >= start && d <= end;
      });

      const employeeMap = {};

      teamBaseRows.forEach((emp) => {
        const id = Number(emp.id);
        if (!id) return;

        employeeMap[id] = {
          id,
          name: emp.name || "Employee",
          achieved: 0,
          hours: 0,
          workedDates: new Set()
        };
      });

      filteredProduction.forEach((row) => {
        const id = Number(row.user_id);
        if (!id) return;

        if (!employeeMap[id]) {
          employeeMap[id] = {
            id,
            name: row.user || "Employee",
            achieved: 0,
            hours: 0,
            workedDates: new Set()
          };
        }

        employeeMap[id].achieved += Number(row.production_count || 0);
        employeeMap[id].hours += Number(row.hours_contributed || 0);
        employeeMap[id].workedDates.add(row.date);
      });

      requestRows.forEach((r) => {
        const id = Number(r.user_id);
        if (!id) return;

        if (!employeeMap[id]) {
          employeeMap[id] = {
            id,
            name: r.user_name || r.user || "Employee",
            achieved: 0,
            hours: 0,
            workedDates: new Set()
          };
        }
      });

      const diffDays =
        Math.max(
          1,
          Math.floor(
            (new Date(endDate).getTime() - new Date(startDate).getTime()) /
              (1000 * 60 * 60 * 24)
          ) + 1
        );

      const teamData = Object.values(employeeMap).map((emp) => {
        const workedDays = emp.workedDates.size;
        const target = workedDays > 0 ? workedDays * DAILY_TARGET : diffDays * DAILY_TARGET;
        const productivity = target ? (emp.achieved / target) * 100 : 0;

        return {
          id: emp.id,
          name: emp.name,
          achieved: emp.achieved,
          hours: emp.hours,
          target,
          productivity: Number(productivity.toFixed(1))
        };
      });

      teamData.sort((a, b) => a.name.localeCompare(b.name));
      setTeamRows(teamData);
    } catch (err) {
      console.error("Manager dashboard load failed:", err);
      setTeamRows([]);
      setRequests([]);
      setAppreciationHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teamRows;
    return teamRows.filter((row) => row.name.toLowerCase().includes(q));
  }, [teamRows, search]);

  const pendingApprovals = useMemo(() => {
    return requests.filter((r) => String(r.status || "").toLowerCase() === "pending").length;
  }, [requests]);

  const totalAchieved = useMemo(() => {
    return filteredEmployees.reduce((sum, row) => sum + Number(row.achieved || 0), 0);
  }, [filteredEmployees]);

  const totalTarget = useMemo(() => {
    return filteredEmployees.reduce((sum, row) => sum + Number(row.target || 0), 0);
  }, [filteredEmployees]);

  const totalHours = useMemo(() => {
    return filteredEmployees.reduce((sum, row) => sum + Number(row.hours || 0), 0);
  }, [filteredEmployees]);

  const avgProductivity = totalTarget
    ? Number(((totalAchieved / totalTarget) * 100).toFixed(1))
    : 0;

  const onTargetCount = filteredEmployees.filter((row) => row.productivity >= 100).length;

  const avgHoursPerEmployee = filteredEmployees.length
    ? Number((totalHours / filteredEmployees.length).toFixed(1))
    : 0;

  const bestPerformer = useMemo(() => {
    if (!filteredEmployees.length) return null;
    const rowsWithWork = filteredEmployees.filter((row) => row.achieved > 0);
    if (!rowsWithWork.length) return null;
    return rowsWithWork.reduce((best, row) =>
      row.productivity > best.productivity ? row : best
    );
  }, [filteredEmployees]);

  const underPerformers = useMemo(() => {
    return filteredEmployees.filter((row) => row.productivity > 0 && row.productivity < 50);
  }, [filteredEmployees]);

  const managerInsight = useMemo(() => {
    if (filteredEmployees.length === 0) {
      return "No employees found in the selected range.";
    }
    if (underPerformers.length > 0) {
      return `Employees currently under performing: ${underPerformers
        .map((e) => e.name)
        .join(", ")}.`;
    }
    if (bestPerformer) {
      return `${bestPerformer.name} is currently leading the team with ${bestPerformer.productivity}% productivity.`;
    }
    return "No performance activity found in the selected range yet.";
  }, [filteredEmployees, underPerformers, bestPerformer]);

  const currentCycleKey = useMemo(() => getHalfCycleKey(new Date()), []);

  const alreadyAppreciatedThisCycle = useMemo(() => {
    if (!bestPerformer) return false;
    return appreciationHistory.some(
      (row) =>
        Number(row.user_id) === Number(bestPerformer.id) &&
        getHalfCycleKey(row.created_at) === currentCycleKey
    );
  }, [bestPerformer, appreciationHistory, currentCycleKey]);

  const topPerformersByCycle = useMemo(() => {
    const rowsWithWork = teamRows.filter((row) => Number(row.achieved || 0) > 0);
    const sorted = [...rowsWithWork].sort((a, b) => {
      if (b.productivity !== a.productivity) return b.productivity - a.productivity;
      if (b.achieved !== a.achieved) return b.achieved - a.achieved;
      return a.name.localeCompare(b.name);
    });

const currentDay = new Date().getDate();

const firstHalf = sorted.slice(0, 10).map((row, index) => ({
  ...row,
  rank: index + 1,
  cycleLabel: "1st Half",
  cycleDate: `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1
  ).padStart(2, "0")}-01`
}));

if (currentDay < 16) {
  return firstHalf;
}

const secondHalf = sorted.slice(0, 10).map((row, index) => ({
  ...row,
  rank: index + 1,
  cycleLabel: "2nd Half",
  cycleDate: `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1
  ).padStart(2, "0")}-16`
}));

return [...firstHalf, ...secondHalf];
  }, [teamRows]);

  const filteredLeaderRows = useMemo(() => {
    const q = leaderSearch.trim().toLowerCase();
    if (!q) return topPerformersByCycle;

    return topPerformersByCycle.filter((row) => {
      const hay = [row.name, row.cycleLabel, row.rank, row.productivity, row.achieved]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [topPerformersByCycle, leaderSearch]);

  const handleResetDates = () => {
    setStartDate(formatDateInput(thirtyDaysAgo));
    setEndDate(formatDateInput(today));
  };

  const handleAppreciate = async () => {
    if (!bestPerformer || alreadyAppreciatedThisCycle) return;

    try {
      setAppreciatingId(bestPerformer.id);

      const cycleStartDay = new Date().getDate() <= 15 ? 1 : 16;

      await api.post("/appreciations", {
        user_id: bestPerformer.id,
        message: `Excellent work ${bestPerformer.name}! Your performance stands out in this period.`,
        cycle_start_day: cycleStartDay
      });

      setSuccessMessage(`Appreciation sent to ${bestPerformer.name}`);
      await loadDashboard();
    } catch (err) {
      console.error(err);
      setErrorMessage(
  err.response?.data?.error || "Failed to send appreciation"
);
    } finally {
      setAppreciatingId(null);
    }
  };

  const handleRowClick = (employee) => {
    if (setSelectedEmployee) {
      setSelectedEmployee(employee.id);
    }
    if (setPage) {
      setPage("employeeDetails");
    }
  };

  const productivityBadge = (value) => {
    if (value >= 100) {
      return {
        text: "On Target",
        bg: "#DCFCE7",
        color: theme.success
      };
    }
    if (value >= 50) {
      return {
        text: "Moderate",
        bg: "#FEF3C7",
        color: theme.warning
      };
    }
    if (value > 0) {
      return {
        text: "Low",
        bg: "#FEE2E2",
        color: theme.danger
      };
    }
    return {
      text: "-",
      bg: "#F1F5F9",
      color: theme.textMuted
    };
  };

  return (
    <>
    {successMessage && (
  <div
    style={{
      position: "fixed",
      top: "24px",
      right: "24px",
      background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
      color: "#fff",
      padding: "14px 18px",
      borderRadius: "14px",
      fontWeight: 700,
      zIndex: 9999,
      boxShadow: "0 12px 30px rgba(15,118,110,0.25)"
    }}
  >
    ✅ {successMessage}
  </div>
)}

{errorMessage && (
  <div
    style={{
      position: "fixed",
      top: "24px",
      right: "24px",
      background: "linear-gradient(135deg, #DC2626 0%, #EF4444 100%)",
      color: "#fff",
      padding: "14px 18px",
      borderRadius: "14px",
      fontWeight: 700,
      zIndex: 9999,
      boxShadow: "0 12px 30px rgba(220,38,38,0.25)"
    }}
  >
    ⚠️ {errorMessage}
  </div>
)}

      <div style={page}>
        <div style={hero}>
          <div>
            <h2 style={title}>Team Overview</h2>
            <p style={subtitle}>
              Track team productivity, search employees, and review performance by date range.
            </p>
          </div>

          <div style={filtersRow}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={dateInput}
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={dateInput}
            />
            <button onClick={handleResetDates} style={resetBtn}>
              Reset
            </button>
            <button onClick={loadDashboard} style={refreshBtn}>
              <HiOutlineArrowPath style={{ fontSize: 15 }} />
              Refresh
            </button>
          </div>
        </div>

        <div style={topGrid}>
          <div style={primaryCard}>
            <div style={primaryHeader}>
              <div>
                <p style={primaryLabel}>Average Productivity</p>
                <h3 style={primaryValue}>{avgProductivity}%</h3>
                <p style={primarySub}>
                  {totalAchieved} achieved out of {totalTarget || 0}
                </p>
              </div>

              <div style={circleWrap}>
                <div style={circleOuter}>
                  <div style={circleInner}>{avgProductivity}%</div>
                </div>
              </div>
            </div>

            <div style={progressTrack}>
              <div
                style={{
                  ...progressFill,
                  width: `${Math.min(avgProductivity, 100)}%`
                }}
              />
            </div>

            <div
              style={{
                ...statusPill,
                background:
                  avgProductivity >= 100
                    ? "#DCFCE7"
                    : avgProductivity >= 50
                    ? "#FEF3C7"
                    : "#FEE2E2",
                color:
                  avgProductivity >= 100
                    ? theme.success
                    : avgProductivity >= 50
                    ? theme.warning
                    : theme.danger
              }}
            >
              {avgProductivity >= 100 ? "On Target" : avgProductivity >= 50 ? "Moderate" : "Low"}
            </div>
          </div>

          <div style={statCard}>
            <div style={iconBubble}>
              <HiOutlineUsers />
            </div>
            <p style={statLabel}>Filtered Employees</p>
            <h3 style={statValue}>{filteredEmployees.length}</h3>
            <p style={statSub}>Based on current search</p>
          </div>

          <div style={statCard}>
            <div style={iconBubble}>
              <HiOutlineChartBar />
            </div>
            <p style={statLabel}>On Target</p>
            <h3 style={statValue}>{onTargetCount}</h3>
            <p style={statSub}>Employees at 100% or more</p>
          </div>
        </div>

        <div style={midGrid}>
          <div style={thinStatCard}>
            <div style={{ ...smallIconBubble, background: "#FEF3C7", color: theme.warning }}>
              <HiOutlineClipboardDocumentCheck />
            </div>
            <div>
              <p style={statLabel}>Pending Approvals</p>
              <h3 style={statValue}>{pendingApprovals}</h3>
            </div>
          </div>

          <div style={thinStatCard}>
            <div style={{ ...smallIconBubble, background: "#ECFEFF", color: theme.primary }}>
              <HiOutlineClock />
            </div>
            <div>
              <p style={statLabel}>Total Hours Logged</p>
              <h3 style={statValue}>{totalHours.toFixed(1)}</h3>
            </div>
          </div>

          <div style={thinStatCard}>
            <div style={{ ...smallIconBubble, background: "#DCFCE7", color: theme.success }}>
              <HiOutlineBolt />
            </div>
            <div>
              <p style={statLabel}>Avg Hours / Employee</p>
              <h3 style={statValue}>{avgHoursPerEmployee}</h3>
            </div>
          </div>
        </div>

        <div style={bottomGrid}>
          <div style={panel}>
            <h3 style={panelTitle}>Team Performance</h3>
            <p style={panelSubtitle}>
              Search employees and click any row to open detailed productivity data.
            </p>

            <div style={searchWrap}>
              <HiOutlineMagnifyingGlass style={searchIcon} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee..."
                style={searchInput}
              />
            </div>

            <table style={table}>
              <thead>
                <tr style={tableHeadRow}>
                  <th style={th}>Employee</th>
                  <th style={th}>Achieved</th>
                  <th style={th}>Target</th>
                  <th style={th}>Productivity</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td style={tdCenter} colSpan="5">
                      Loading...
                    </td>
                  </tr>
                ) : filteredEmployees.length === 0 ? (
                  <tr>
                    <td style={tdCenter} colSpan="5">
                      No employees found
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((row, index) => {
                    const badge = productivityBadge(row.productivity);
                    return (
                      <tr
                        key={row.id}
                        style={{
                          background: index % 2 === 0 ? "#FFFFFF" : "#F8FAFC",
                          cursor: "pointer"
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        <td style={employeeTd}>{row.name}</td>
                        <td style={td}>{row.achieved}</td>
                        <td style={td}>{row.target}</td>
                        <td
                          style={{
                            ...td,
                            color:
                              row.productivity > 0 && row.productivity < 50
                                ? theme.danger
                                : theme.text
                          }}
                        >
                          {row.productivity > 0 ? `${row.productivity}%` : "-"}
                        </td>
                        <td style={td}>
                          <span
                            style={{
                              ...tableStatusPill,
                              background: badge.bg,
                              color: badge.color
                            }}
                          >
                            {badge.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={rightColumn}>
            <div style={panel}>
              <div style={highlightsHeaderRow}>
                <div>
                  <h3 style={panelTitle}>Performance Highlights</h3>
                  <p style={panelSubtitle}>Key outcomes for the selected date range</p>
                </div>

                <button type="button" style={leadersBtn} onClick={() => setLeadersOpen(true)}>
                  <HiOutlineListBullet style={{ fontSize: 16 }} />
                  Top Performers
                </button>
              </div>

              <div style={highlightsBody}>
                <div style={highlightBox}>
                  <div style={highlightHeader}>
                    <div style={{ ...highlightIconWrap, background: "#ECFDF5", color: theme.success }}>
                      <HiOutlineTrophy />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={highlightLabel}>Best Performer</p>
                      <h4 style={highlightName}>{bestPerformer ? bestPerformer.name : "-"}</h4>
                      <p style={highlightSub}>
                        {bestPerformer ? `${bestPerformer.productivity}% productivity` : "No data"}
                      </p>
                      {bestPerformer && (
                        <p style={cycleNote}>
                          Appreciation window: {new Date().getDate() <= 15 ? "1st to 15th" : "16th to month end"}
                        </p>
                      )}
                    </div>
                  </div>

                  {bestPerformer && (
                    <button
                      onClick={handleAppreciate}
                      style={{
                        ...appreciateBtn,
                        opacity:
                          appreciatingId === bestPerformer.id || alreadyAppreciatedThisCycle ? 0.65 : 1,
                        cursor:
                          appreciatingId === bestPerformer.id || alreadyAppreciatedThisCycle
                            ? "not-allowed"
                            : "pointer"
                      }}
                      disabled={appreciatingId === bestPerformer.id || alreadyAppreciatedThisCycle}
                    >
                      <HiOutlineHandThumbUp style={{ fontSize: 16 }} />
                      {appreciatingId === bestPerformer.id
                        ? "Sending..."
                        : alreadyAppreciatedThisCycle
                        ? "Already Appreciated"
                        : "Appreciate"}
                    </button>
                  )}
                </div>

                <div style={highlightBoxNoMargin}>
                  <div style={highlightHeader}>
                    <div style={{ ...highlightIconWrap, background: "#FEF2F2", color: theme.danger }}>
                      <HiOutlineExclamationTriangle />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={highlightLabel}>Under Performers</p>
                      <h4 style={highlightName}>
                        {underPerformers.length
                          ? underPerformers.map((emp) => emp.name).join(", ")
                          : "None"}
                      </h4>
                      <p style={highlightSub}>
                        Employees below 50% productivity in selected range
                      </p>
                    </div>
                  </div>

                  {underPerformers.length > 0 && (
                    <div style={chipsWrap}>
                      {underPerformers.map((emp) => (
                        <span key={emp.id} style={chipDanger}>
                          {emp.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={insightPanelInline}>
              <div style={insightHeader}>
                <HiOutlineSparkles style={{ fontSize: 18, color: theme.primary }} />
                <h3 style={insightTitle}>Manager Insight</h3>
              </div>

              <p style={insightText}>{managerInsight}</p>
            </div>
          </div>
        </div>
      </div>

      {leadersOpen && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div>
                <h3 style={modalTitle}>Top Performers List</h3>
                <p style={modalSubtitle}>
                  1st-half and 2nd-half monthly leaderboard.
                </p>
              </div>

              <button type="button" style={closeBtn} onClick={() => setLeadersOpen(false)}>
                <HiOutlineXMark />
              </button>
            </div>

            <div style={modalSearchWrap}>
              <HiOutlineMagnifyingGlass style={searchIcon} />
              <input
                value={leaderSearch}
                onChange={(e) => setLeaderSearch(e.target.value)}
                placeholder="Search employee, cycle, rank..."
                style={searchInput}
              />
            </div>

            <div style={modalList}>
              {filteredLeaderRows.length === 0 ? (
                <div style={emptyModalState}>No top performers found</div>
              ) : (
                filteredLeaderRows.map((row) => (
                  <div key={`${row.cycleLabel}-${row.rank}-${row.id}`} style={leaderRow}>
                    <div style={leaderLeft}>
                      <div style={leaderRank}>#{row.rank}</div>
                      <div>
                        <div style={leaderName}>{row.name}</div>
                        <div style={leaderMeta}>
                          {row.cycleLabel} • {formatDisplayDate(row.cycleDate)}
                        </div>
                      </div>
                    </div>

                    <div style={leaderRight}>
                      <span style={leaderMetric}>{row.productivity}%</span>
                      <span style={leaderMetricSub}>{row.achieved} achieved</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ================= STYLES ================= */

const page = {
  maxWidth: "1600px",
  margin: "0 auto",
  color: theme.text,
  fontFamily: "sans-serif"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  marginBottom: "18px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 700
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const filtersRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  alignItems: "center"
};

const dateInput = {
  height: "40px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "0 12px",
  fontSize: "14px",
  outline: "none"
};

const resetBtn = {
  height: "40px",
  borderRadius: "12px",
  border: "none",
  background: "#E2E8F0",
  color: theme.text,
  padding: "0 16px",
  fontWeight: 700,
  cursor: "pointer"
};

const refreshBtn = {
  height: "40px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#fff",
  padding: "0 16px",
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px"
};

const topGrid = {
  display: "grid",
  gridTemplateColumns: "1.35fr 1fr 1fr",
  gap: "14px",
  marginBottom: "14px"
};

const midGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: "14px",
  marginBottom: "16px"
};

const bottomGrid = {
  display: "grid",
  gridTemplateColumns: "1.35fr 1fr",
  gap: "16px",
  alignItems: "stretch"
};

const primaryCard = {
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  borderRadius: "20px",
  padding: "16px",
  color: "#fff",
  boxShadow: "0 12px 26px rgba(15,118,110,0.18)"
};

const primaryHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "center",
  marginBottom: "14px"
};

const primaryLabel = {
  margin: 0,
  fontSize: "13px",
  fontWeight: 700,
  color: "rgba(255,255,255,0.9)"
};

const primaryValue = {
  margin: "8px 0 6px 0",
  fontSize: "22px",
  fontWeight: 800
};

const primarySub = {
  margin: 0,
  fontSize: "13px",
  color: "rgba(255,255,255,0.9)"
};

const circleWrap = {
  flexShrink: 0
};

const circleOuter = {
  width: "70px",
  height: "70px",
  borderRadius: "50%",
  background: "conic-gradient(#ffffff 24deg, rgba(255,255,255,0.25) 0deg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const circleInner = {
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.9)",
  color: theme.primaryDark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: "12px"
};

const progressTrack = {
  width: "100%",
  height: "8px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.25)",
  overflow: "hidden",
  marginBottom: "10px"
};

const progressFill = {
  height: "100%",
  borderRadius: "999px",
  background: "#ffffff"
};

const statusPill = {
  width: "fit-content",
  padding: "5px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  marginLeft: "auto"
};

const statCard = {
  background: theme.surface,
  borderRadius: "18px",
  padding: "16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 8px 22px rgba(15,23,42,0.05)"
};

const thinStatCard = {
  background: theme.surface,
  borderRadius: "18px",
  padding: "14px 16px",
  border: "1px solid #F1F5F9",
  boxShadow: "0 8px 22px rgba(15,23,42,0.05)",
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const iconBubble = {
  width: "38px",
  height: "38px",
  borderRadius: "12px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  marginBottom: "12px"
};

const smallIconBubble = {
  width: "38px",
  height: "38px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  flexShrink: 0
};

const statLabel = {
  margin: 0,
  fontSize: "12px",
  fontWeight: 700,
  color: theme.textMuted
};

const statValue = {
  margin: "6px 0 0 0",
  fontSize: "20px",
  fontWeight: 800
};

const statSub = {
  margin: "6px 0 0 0",
  fontSize: "12px",
  color: theme.textMuted
};

const panel = {
  ...cardStyle,
  border: "1px solid #F1F5F9",
  minHeight: "485px",
};

const panelTitle = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700
};

const panelSubtitle = {
  margin: "6px 0 16px 0",
  color: theme.textMuted,
  fontSize: "13px"
};

const searchWrap = {
  position: "relative",
  marginBottom: "14px"
};

const searchIcon = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "18px"
};

const searchInput = {
  width: "100%",
  height: "42px",
  borderRadius: "14px",
  border: "1px solid #CBD5E1",
  padding: "0 14px 0 40px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const table = {
  width: "100%",
  borderCollapse: "collapse"
};

const tableHeadRow = {
  background: "#F1F5F9"
};

const th = {
  textAlign: "left",
  padding: "14px 12px",
  fontSize: "13px",
  fontWeight: 700,
  color: theme.text
};

const td = {
  padding: "14px 12px",
  borderBottom: "1px solid #EEF2F7",
  fontSize: "14px",
  color: theme.text
};

const employeeTd = {
  ...td,
  color: theme.primaryDark,
  fontWeight: 700
};

const tdCenter = {
  ...td,
  textAlign: "center",
  color: theme.textMuted
};

const tableStatusPill = {
  display: "inline-block",
  padding: "6px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700
};

const rightColumn = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "12px",
  height: "100%"
};

const highlightsHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "10px",
  flexWrap: "wrap"
};

const leadersBtn = {
  height: "38px",
  borderRadius: "12px",
  border: "1px solid #CCFBF1",
  background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
  color: "#FFFFFF",
  padding: "0 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15,118,110,0.18)",
  transition: "0.2s ease"
};

const highlightBox = {
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "14px 16px",
  background: "#FCFDFE",
  marginBottom: "12px"
};

const highlightBoxNoMargin = {
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "14px 16px",
  background: "#FCFDFE"
};

const highlightHeader = {
  display: "flex",
  gap: "12px",
  alignItems: "flex-start"
};

const highlightIconWrap = {
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  flexShrink: 0
};

const highlightLabel = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "12px",
  fontWeight: 700
};

const highlightName = {
  margin: "4px 0",
  fontSize: "16px",
  fontWeight: 800
};

const highlightSub = {
  margin: 0,
  color: theme.textMuted,
  fontSize: "13px"
};

const cycleNote = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "12px",
  fontWeight: 600
};

const chipsWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "14px"
};

const chipDanger = {
  display: "inline-block",
  padding: "7px 12px",
  borderRadius: "999px",
  background: "#FEE2E2",
  color: theme.danger,
  fontSize: "12px",
  fontWeight: 700
};

const insightPanelInline = {
  background: "linear-gradient(180deg, #ECFEFF 0%, #FFFFFF 100%)",
  border: `1px solid ${theme.border}`,
  borderRadius: "20px",
  padding: "18px"
};

const insightHeader = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "12px"
};

const insightTitle = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 700
};

const insightText = {
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.6
};

const appreciateBtn = {
  marginTop: "14px",
  height: "40px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #16A34A 0%, #22C55E 100%)",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  padding: "0 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px"
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.36)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 1200
};

const modalCard = {
  width: "100%",
  maxWidth: "760px",
  background: "#FFFFFF",
  borderRadius: "22px",
  border: "1px solid #E2E8F0",
  boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
  padding: "18px",
  maxHeight: "82vh",
  display: "flex",
  flexDirection: "column"
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px",
  marginBottom: "14px"
};

const modalTitle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: 800,
  color: theme.text
};

const modalSubtitle = {
  margin: "6px 0 0 0",
  fontSize: "13px",
  color: theme.textMuted
};

const closeBtn = {
  width: "38px",
  height: "38px",
  borderRadius: "12px",
  border: "none",
  background: theme.surfaceSoft,
  color: theme.primaryDark,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  flexShrink: 0
};

const modalSearchWrap = {
  position: "relative",
  marginBottom: "14px"
};

const modalList = {
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  paddingRight: "4px"
};

const leaderRow = {
  border: "1px solid #E2E8F0",
  borderRadius: "14px",
  padding: "14px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  background: "#FCFDFE"
};

const leaderLeft = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const leaderRank = {
  minWidth: "40px",
  height: "40px",
  borderRadius: "999px",
  background: "#ECFEFF",
  color: theme.primaryDark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800
};

const leaderName = {
  fontSize: "15px",
  fontWeight: 800,
  color: theme.text
};

const leaderMeta = {
  marginTop: "4px",
  fontSize: "12px",
  color: theme.textMuted
};

const leaderRight = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "4px"
};

const leaderMetric = {
  fontSize: "16px",
  fontWeight: 800,
  color: theme.primaryDark
};

const leaderMetricSub = {
  fontSize: "12px",
  color: theme.textMuted
};

const emptyModalState = {
  textAlign: "center",
  color: theme.textMuted,
  padding: "24px 12px"
};

const highlightsBody = {
  display: "flex",
  flexDirection: "column",
  gap: "12px"
};

export default ManagerDashboard;